import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';

interface DrawerShellProps {
  open: boolean;
  title: string | React.ReactNode;
  onRequestClose: () => void;
  onNext: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onBack?: () => void;
  nextTitle?: string;
  backTitle?: string;
  cancelTitle?: string;
  cancelDisabled?: boolean;
  onCustomCancel?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  cancelButtonState?: 'default' | 'danger';
  nextButtonState?: 'default' | 'danger';
  nextDisabled: boolean;
  children: React.ReactNode;
  testId?: string;
  disableContentScroll?: boolean;
}

export const DrawerShell: React.FC<DrawerShellProps> = ({
  open,
  title,
  onRequestClose,
  onNext,
  nextDisabled,
  nextButtonState = 'default',
  children,
  nextTitle,
  onBack,
  backTitle,
  cancelTitle,
  cancelDisabled,
  onCustomCancel,
  cancelButtonState = 'default',
  testId = 'drawer',
  disableContentScroll = false,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const sp = tokens.semantic.spacing.desktop.gap;
  const color = tokens.semantic.color;

  const blurActiveElement = () => {
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }
  };

  const handleRequestClose = () => {
    blurActiveElement();
    onRequestClose();
  };

  const handleNext = (event: React.MouseEvent<HTMLButtonElement>) => {
    blurActiveElement();
    onNext(event);
  };

  const handleCustomCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
    blurActiveElement();
    (onCustomCancel || onRequestClose)(event);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleRequestClose}
      ModalProps={{ keepMounted: false }}
      data-testid={testId}
      sx={{
        '& .MuiDrawer-paper': {
          width: '567px',
          padding: sp[3].value,
          backgroundColor: color.surface.default.value,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: `100vh`,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: '24px',
            fontWeight: tokens.semantic.fontWeight.emphasis.value,
            color: color.type.default.value,
          }}
        >
          {title}
        </Typography>
        <IconButton
          onClick={handleRequestClose}
          aria-label="close"
          data-testid={`${testId}-close`}
          sx={{
            color: color.action.secondary.onSecondary.value,
            '&:hover': {
              backgroundColor: color.action.secondary.hoverFill.value,
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          overflowY: disableContentScroll ? 'hidden' : 'auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          padding: sp[1].value,
        }}
      >
        {children}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: sp[3].value,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          disabled={cancelDisabled}
          onClick={handleCustomCancel}
          data-testid={`${testId}-cancel-button`}
          sx={{
            textTransform: 'none',
            minWidth: '88px',
            height: '40px',
            cursor: 'pointer',
            borderRadius: tokens.semantic.radius.lg.value,
            borderColor:
              cancelButtonState === 'danger'
                ? color.action.destructive.default.value
                : color.type.default.value,
            color:
              cancelButtonState === 'danger'
                ? color.action.destructive.default.value
                : color.type.default.value,
            '&:not(:disabled):hover':
              cancelButtonState === 'danger'
                ? {
                    borderColor: color.action.destructive.active.value,
                    color: color.action.destructive.active.value,
                    backgroundColor:
                      color.action.destructive.onDestructiveDisabled.value,
                  }
                : {},
          }}
        >
          {cancelTitle || 'Cancel'}
        </Button>
        <Box sx={{ display: 'flex', gap: sp[2].value }}>
          {onBack && (
            <Button
              variant="text"
              onClick={onBack}
              data-testid={`${testId}-back-button`}
              sx={{
                textTransform: 'none',
                minWidth: '88px',
                height: '40px',
                cursor: 'pointer',
                borderRadius: tokens.semantic.radius.lg.value,
                borderColor: color.type.default.value,
                color: color.type.default.value,
                '&:hover': {
                  borderColor: color.type.default.value,
                  backgroundColor: 'transparent',
                },
              }}
            >
              {backTitle || 'Previous'}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={nextDisabled}
            data-testid={`${testId}-save-button`}
            disableElevation
            sx={{
              textTransform: 'none',
              minWidth: '88px',
              height: '40px',
              cursor: 'pointer',
              borderRadius: tokens.semantic.radius.lg.value,
              backgroundColor:
                nextButtonState === 'danger'
                  ? color.action.destructive.default.value
                  : color.type.default.value,
              color: color.surface.default.value,
              '&:hover': {
                backgroundColor:
                  nextButtonState === 'danger'
                    ? color.action.destructive.hover.value
                    : color.type.default.value,
                opacity: 0.9,
              },
              '&.Mui-disabled': {
                cursor: 'not-allowed',
                backgroundColor: color.ui.divider.default.value,
                color: color.type.muted.value,
              },
              borderColor:
                nextButtonState === 'danger'
                  ? color.action.destructive.default.value
                  : color.type.default.value,
            }}
          >
            {nextTitle || 'Save'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
